import { useSnackbar } from 'hooks';
import { SatelliteApi } from 'middleware/SatelliteApi/api';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import useMountStatus from './useMountStatus';

const useJobPolling = (params, _delay) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const {
    Job: {
      actions: { pagedJobs },
    },
  } = SatelliteApi;

  // isMounted is put into a ref so that it can be used in the callback without causing a re-render after every fetch
  const isMounted = useMountStatus();
  const mount = useRef(isMounted);
  const [delay, setDelay] = useState(_delay);
  const savedCallback = useRef();
  const timeout = useRef();

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        setDelay(null);
      } else {
        setDelay(_delay);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [_delay]);

  const callback = useCallback(() => {
    dispatch(
      pagedJobs({
        queryParams: { ...params },
        successCallback: (response) => {
          if (!mount.current()) return;
          clearTimeout(timeout.current);
          params.setJobState(response);
          if (delay) timeout.current = setTimeout(() => savedCallback.current(), delay);
        },
        failureCallback: (response) => {
          enqueueSnackbar(response?.error?.message || 'Error fetching jobs.');
        },
      })
    );
  }, [params, delay, dispatch, enqueueSnackbar, pagedJobs, mount]);

  useEffect(() => {
    savedCallback.current = callback;
    clearTimeout(timeout.current);
    callback();
  }, [callback]);

  useEffect(() => {
    clearTimeout(timeout.current);
    if (delay !== null) {
      savedCallback.current();
    }
    return () => clearTimeout(timeout.current);
  }, [delay]);
};

export default useJobPolling;
